import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { z } from "zod";
import { ApprovalIssueSchema } from "@erp/shared-types";

/**
 * Sales Orders v3 — read-only browse over the OMS v3 relational draft tables
 * (`ar_draft_sales_orders` + `ar_draft_sales_order_line_items`). Powers the
 * admin-only Sales Orders v3 pages. Distinct from the `draftSalesOrders` group
 * (per-line/header editing) — this group is list + read-only detail.
 */

const UUIDSchema = z.string().uuid();
// DECIMAL columns come back from Sequelize as strings; accept either on the wire.
const NumericSchema = z.union([z.string(), z.number()]);

/**
 * Coarse v3-native lifecycle position, rolled up from the draft's status /
 * salesOrderId / approval decisions (see `rollupLifecycleStage` in
 * `@erp/shared-types`). Drives the list stage badge + the review-page stepper.
 */
// Keep in sync with `OrderLifecycleStage` in `@erp/shared-types`.
const LifecycleStageSchema = z.enum([
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
  "under_cs_review",
  "rejected",
  "superseded",
]);
// Stages grow (`under_cs_review` joined the branches), and every response
// carries one. x-extensible-enum tells oasdiff the set is allowed to grow in
// responses; requests keep the closed enum.
JSON_SCHEMA_OUTPUT_REGISTRY.add(LifecycleStageSchema, {
  enum: undefined,
  "x-extensible-enum": LifecycleStageSchema.options,
} as { enum: undefined });

const V3LineItemSchema = z
  .object({
    id: UUIDSchema,
    lineNumber: z.number().int(),
    customerIdentifier: z.string().nullable().optional(),
    customerDescription: z.string().nullable().optional(),
    quantity: NumericSchema.nullable().optional(),
    unitPrice: NumericSchema.nullable().optional(),
    amount: NumericSchema.nullable().optional(),
    unit: z.string().nullable().optional(),
    warehouse: z.string().nullable().optional(),
    inventoryStatus: z.string().nullable().optional(),
    /** Fixed lead time (days) stamped by the lead-time extractor at ingest. */
    leadTimeDays: z.number().int().nullable().optional(),
    listPrice: NumericSchema.nullable().optional(),
    listPriceMismatch: z.boolean().nullable().optional(),
    /**
     * Stamped at submission: `excludedFromOrder: true` marks an exception item
     * the ERP order was submitted without (unmatched, inactive, ERP-rejected).
     */
    approvalIssue: ApprovalIssueSchema.nullable().optional(),
    // The matched product variant — needed to scope a bulk discount to the line.
    selectedProductVariantId: UUIDSchema.nullable().optional(),
    // The matched customer product; a line counts as "matched" when either
    // selected id is set (a CustomerProduct can carry a null variant id).
    selectedCustomerProductId: UUIDSchema.nullable().optional(),
    /** Internal SKU of the matched product variant (detail endpoint only). */
    productSku: z.string().nullable().optional(),
    additionalData: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .strip();

/** Read-only detail of a draft sales order (header + lines). */
const V3DetailSchema = z
  .object({
    id: UUIDSchema,
    organizationId: UUIDSchema,
    customerId: UUIDSchema.nullable().optional(),
    shipToLocationId: UUIDSchema.nullable().optional(),
    /**
     * The customer's usual ship-to, suggested (never bound) when the PO named
     * no delivery address. Null once a ship-to is set or when there is no
     * consistent history. Drives the one-click "Use …" on the ship-to field.
     */
    suggestedShipToLocation: z
      .object({ id: UUIDSchema, locationName: z.string() })
      .nullable()
      .optional()
      .default(null),
    billToLocationId: UUIDSchema.nullable().optional(),
    purchaseOrderNumber: z.string().nullable().optional(),
    status: z.string(),
    source: z.string().nullable().optional(),
    orderDate: z.string().nullable().optional(),
    receivedDate: z.string().nullable().optional(),
    shipDate: z.string().nullable().optional(),
    deliveryDate: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    totalAmount: NumericSchema.nullable().optional(),
    currency: z.string().nullable().optional(),
    salesOrderId: UUIDSchema.nullable().optional(),
    /** Rolled-up lifecycle position for the review-page stepper. */
    lifecycleStage: LifecycleStageSchema,
    /**
     * Shared split-group key (null unless this draft is part of a split) and the
     * number of drafts in that group. Drive the header "Split ×N" badge; show it
     * only when `splitGroupId != null && splitGroupSize > 1`.
     */
    splitGroupId: UUIDSchema.nullable(),
    splitGroupSize: z.int().nonnegative(),
    /** ERP order number once assigned (EBS assigns it asynchronously via
     * polling; registered-API submissions return it synchronously). */
    erpOrderNumber: z.string().nullable().optional().default(null),
    /**
     * True when the draft was submitted through a registered ERP submission
     * API (a DraftSalesOrderSubmission record exists for a non-builtin API).
     * Gates the registered-path "Poll for ERP ID" action.
     */
    hasRegisteredErpSubmission: z.boolean().optional().default(false),
    /**
     * True when the draft completed with nothing sent to the ERP: approved
     * without ERP, or every line was an exception item. No ERP number will
     * ever arrive, so the header says so instead of "Pending…".
     */
    erpSubmissionSkipped: z.boolean().optional().default(false),
    /**
     * PO-revision lineage. `revisionNumber` starts at 1; `revisionOfDraftId`
     * points at the draft this one replaced; `supersededByDraftId` is set on a
     * draft that a newer revision replaced. Drive the header "Rev N" badge and
     * the revision banners.
     */
    revisionNumber: z.int().positive(),
    revisionOfDraftId: UUIDSchema.nullable(),
    supersededByDraftId: UUIDSchema.nullable(),
    /**
     * The draft that revises this one, when any exists — set both for
     * superseded drafts (same as `supersededByDraftId`) and for submitted
     * drafts that received a post-submission revision (which are NOT
     * superseded). Drives the revision banners.
     */
    revisedByDraftId: UUIDSchema.nullable(),
    /**
     * Rejection details — set while `status` is REJECTED. `rejectionAwaitingRevision`
     * distinguishes "rejected, waiting on a new PO" from a flat rejection.
     */
    rejectionReason: z.string().nullable().optional().default(null),
    rejectionAwaitingRevision: z.boolean().optional().default(false),
    /** Resolved header values (rawData.computedData) keyed by field id. */
    computedData: z.record(z.string(), z.unknown()).nullable().optional(),
    lineItems: z.array(V3LineItemSchema),
  })
  .strip();

/**
 * One revision in a draft's revision chain (oldest first), for the review-page
 * "Rev N" popover. Single-revision drafts return a one-member chain.
 */
const RevisionChainMemberSchema = z.object({
  id: UUIDSchema,
  revisionNumber: z.int().positive(),
  purchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  lifecycleStage: LifecycleStageSchema,
  totalAmount: NumericSchema.nullable(),
  currency: z.string().nullable(),
  receivedDate: z.string().nullable(),
  /** True for the draft the request was made from. */
  isCurrent: z.boolean(),
});

/** One member of a split group, for the review-page sibling list. */
const SplitGroupMemberSchema = z.object({
  id: UUIDSchema,
  purchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  lifecycleStage: LifecycleStageSchema,
  totalAmount: NumericSchema.nullable(),
  currency: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  /** The split rule that produced this member (null for the original draft). */
  splitRule: z.string().nullable(),
  /** True for the draft the request was made from. */
  isCurrent: z.boolean(),
});

/** Row shape for the v3 list table. */
const V3ListItemSchema = z.object({
  /** True while the last ERP submission attempt failed (cleared on success). */
  submissionFailed: z.boolean().optional().default(false),
  id: UUIDSchema,
  customerId: UUIDSchema.nullable(),
  customerName: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  status: z.string(),
  /** Rolled-up lifecycle position for the list stage badge. */
  lifecycleStage: LifecycleStageSchema,
  source: z.string().nullable(),
  receivedDate: z.string().nullable(),
  orderDate: z.string().nullable(),
  totalAmount: NumericSchema.nullable(),
  currency: z.string().nullable(),
  lineItemCount: z.int().nonnegative(),
  shipToLocationName: z.string().nullable(),
  originId: z.string().nullable(),
  salesOrderOriginId: z.string().nullable(),
  /** Set when this draft belongs to a split — drives the list split indicator. */
  splitGroupId: UUIDSchema.nullable(),
  /** PO-revision lineage — drive the list "Rev N" / superseded indicators. */
  revisionNumber: z.int().positive(),
  supersededByDraftId: UUIDSchema.nullable(),
  /** Rejected while waiting on a replacement PO — drives the badge label. */
  rejectionAwaitingRevision: z.boolean(),
  /**
   * How the order left the draft stage: ERP adapter sales order, record-path
   * submission through an ERP API, or approved without any ERP call. Null
   * while still a draft. Lets exports tell "Submitted" badges apart.
   */
  submissionPath: z
    .enum(["adapter", "registered_api", "approve_only"])
    .nullable()
    .optional()
    .default(null),
});

/** Resolved review-page presentation config (metrics / columns / header fields). */
const SalesOrderUiConfigSchema = z.object({
  metrics: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      tooltip: z.string(),
      format: z.enum([
        "currency",
        "percent",
        "integer",
        "number",
        "date",
        "string",
      ]),
      requiresCost: z.boolean(),
      // Custom (org-added) metrics carry an aggregation + a line-field path or a
      // two-field compute that the frontend aggregates across the order's lines;
      // catalog metrics omit these.
      aggregation: z.enum(["count", "sum", "average"]).optional(),
      dataPath: z.string().optional(),
      compute: z
        .object({
          left: z.string(),
          op: z.enum(["subtract", "add", "multiply", "divide", "percent_off"]),
          right: z.string(),
        })
        .optional(),
      decimals: z.number().int().min(0).max(10).optional(),
    }),
  ),
  lineColumns: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      width: z.string().nullable(),
      alignRight: z.boolean(),
      // Effective alignment (org override → catalog default → left). Optional
      // on the wire for compatibility with cached pre-align payloads.
      align: z.enum(["left", "center", "right"]).optional(),
      // Custom (org-added) columns carry a dot-path into the line item +
      // optional format; built-in catalog columns omit both.
      dataPath: z.string().optional(),
      format: z.string().optional(),
      // Computed custom column: two operand dot-paths + an operator
      // (percent_off = (right − left) / right × 100).
      compute: z
        .object({
          left: z.string(),
          op: z.enum(["subtract", "add", "multiply", "divide", "percent_off"]),
          right: z.string(),
        })
        .optional(),
      decimals: z.number().int().min(0).max(10).optional(),
      // Help text shown as a tooltip on the column header.
      tooltip: z.string().optional(),
      // Inline-editable cells (manual override); only honored for writable
      // data paths (EDITABLE_LINE_DATA_PATHS in @erp/shared-types).
      editable: z.boolean().optional(),
      // Optional second line under the main value: short prefix + a field or
      // two-field compute (e.g. "VJ: $34,506.61" under the PO price).
      subline: z
        .object({
          prefix: z.string().optional(),
          dataPath: z.string().optional(),
          compute: z
            .object({
              left: z.string(),
              op: z.enum([
                "subtract",
                "add",
                "multiply",
                "divide",
                "percent_off",
              ]),
              right: z.string(),
            })
            .optional(),
          format: z.string().optional(),
          decimals: z.number().int().min(0).max(10).optional(),
        })
        .optional(),
    }),
  ),
  headerFields: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      dataPath: z.string().optional(),
      control: z.string().optional(),
      format: z.string().optional(),
      editable: z.boolean().optional(),
      // No upper bound: widening the prior max (4) tripped the OpenAPI
      // breaking-change check; the value is an internal grid hint (1=¼ … 5=⅓).
      colSpan: z.number().int().min(1).optional(),
      // Selectable options for `control: "select"` fields (e.g. shipping method /
      // freight terms from the org's core enums). Empty/absent → rendered as text.
      options: z
        .array(z.object({ value: z.string(), label: z.string() }))
        .optional(),
      // Help text shown as a tooltip next to the field label.
      tooltip: z.string().optional(),
    }),
  ),
});

/**
 * One discovered, resolvable dot-path from sampling recent orders — surfaced in
 * the admin editor's data-path picker. `example` is a stringified, truncated
 * sample value; `coverage` is how many sampled records (orders for header paths,
 * lines for line paths) actually had the path populated.
 */
const FieldPathInfoSchema = z.object({
  path: z.string(),
  example: z.string().nullable(),
  type: z.string(),
  coverage: z.int().nonnegative(),
  sampled: z.int().nonnegative(),
});

const SampleFieldPathsInputSchema = z.object({
  limit: z.int().min(1).max(50).optional(),
});

const SampleFieldPathsResponseSchema = z.object({
  header: z.array(FieldPathInfoSchema),
  lineItem: z.array(FieldPathInfoSchema),
  sampledOrders: z.int().nonnegative(),
});

const V3PaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

const ListInputSchema = z.object({
  // Loose strings (not enums) so any value from the shared filters bar passes;
  // unsupported values simply match nothing.
  status: z.array(z.string()).optional(),
  // Rolled-up lifecycle stage(s) — what the list's Stage column shows. Filtered
  // server-side by mirroring `rollupLifecycleStage`. Loose strings; unknown
  // values match nothing. Values: draft | creating_discounts | approving |
  // ready | created | under_cs_review | rejected | superseded.
  stage: z.array(z.string()).optional(),
  source: z.array(z.string()).optional(),
  customerId: z.array(UUIDSchema).optional(),
  // Hide these customers' orders. Drafts with no customer assigned yet are
  // kept (same semantics as the legacy v2 list's excludedCustomerId).
  excludedCustomerId: z.array(UUIDSchema).optional(),
  // Orders for the customers this person is assigned (Members → Data Access →
  // "View sales orders"). Resolved server-side from their sales assignment so
  // a large customer set never has to travel through the URL.
  salesAssigneeUserId: UUIDSchema.optional(),
  // Only orders submitted to the ERP whose ERP order number has not come back
  // yet (adapter sales order with no origin id, or a registered-API submission
  // still awaiting its number) — the ones the ERP Order # column shows as
  // "Pending".
  erpNumberPending: z.boolean().optional(),
  search: z.string().optional(),
  // Inclusive `receivedDate` calendar-day bounds (ISO `YYYY-MM-DD`, UTC days).
  // Either side may be omitted for an open-ended range.
  receivedFrom: z.string().date().optional(),
  receivedTo: z.string().date().optional(),
  // Inclusive calendar-date window on `dateBasis` (default receivedDate).
  // `YYYY-MM-DD`; an ISO datetime is truncated to its UTC date.
  dateBasis: z.enum(["receivedDate", "orderDate", "createdAt"]).optional(),
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  sortBy: z
    .enum(["receivedDate", "orderDate", "totalAmount", "status"])
    .optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

const ListResponseSchema = z.object({
  draftSalesOrders: z.array(V3ListItemSchema),
  pagination: V3PaginationSchema,
});

// Same filters + sort as `list`, no pagination — the server caps the row count.
const ExportInputSchema = ListInputSchema.omit({ limit: true, offset: true });

const ExportResponseSchema = z.object({
  rows: z.array(V3ListItemSchema),
  totalRows: z.int().nonnegative(),
  /** True when the filtered set exceeded the export cap and was truncated. */
  truncated: z.boolean(),
});

export const salesOrdersV3Contracts = {
  list: oc.input(ListInputSchema).output(ListResponseSchema).route({
    summary: "List sales orders",
    description:
      "Lists incoming sales orders (drafts) with filters for status, stage, source, customer, assigned salesperson, date range, and search; sorted and paginated, with totals and stage badges.",
    method: "GET",
  }),
  export: oc.input(ExportInputSchema).output(ExportResponseSchema).route({
    summary: "Export filtered sales orders",
    description:
      "Returns all sales orders matching the list filters and sort, without pagination, for spreadsheet export; capped, with a flag when results were truncated.",
    method: "GET",
  }),
  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(
      z.object({
        draftSalesOrder: V3DetailSchema,
        uiConfig: SalesOrderUiConfigSchema,
      }),
    )
    .route({
      summary: "Get a sales order for review",
      description:
        "Returns one sales order draft with header, line items, lifecycle stage, ERP order number, and resolved values, plus the organization's review-page layout config.",
      method: "GET",
    }),
  // The org's resolved review-page presentation config, standalone (no draft) —
  // powers the Quote Builder totals strip. Presentational data only, so any
  // authenticated org user may read it.
  getUiConfig: oc.input(z.object({})).output(SalesOrderUiConfigSchema).route({
    summary: "Get sales order review page config",
    description:
      "Returns the organization's configured review-page metrics, line columns, and header fields, without loading a specific order. Read-only.",
    method: "GET",
  }),
  // All members of the draft's split group (the original + every child), for the
  // review-page sibling list. Empty when the draft was never split.
  getSplitGroup: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ members: z.array(SplitGroupMemberSchema) }))
    .route({
      summary: "List orders split from a sales order",
      description:
        "Returns the original order and every order split from it, with the split rule that produced each. Empty when the order was never split.",
      method: "GET",
    }),
  // The draft's full revision chain (oldest → newest), walking
  // revisionOfDraftId backward and supersededByDraftId forward. One member for
  // drafts that were never revised.
  getRevisionChain: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ members: z.array(RevisionChainMemberSchema) }))
    .route({
      summary: "Get a sales order's revision history",
      description:
        "Returns every revision of a sales order oldest to newest, following replaced and replacing purchase-order versions. Single member when never revised.",
      method: "GET",
    }),
  // Sample recent orders for the org and return the resolvable dot-paths (header
  // + line item, incl. dynamic additionalData.* keys) with example values — the
  // data-path picker in the review-page config editor. Read-gated like get/list.
  sampleFieldPaths: oc
    .input(SampleFieldPathsInputSchema)
    .output(SampleFieldPathsResponseSchema)
    .route({
      summary: "Sample available sales order field paths",
      description:
        "Samples recent orders and returns the header and line-item data paths found, with example values, types, and coverage, for configuring the review page. Admin only.",
      method: "GET",
    }),
  // Re-home a draft to another organization (OMS v3 routing). Gated by the
  // Execute:SalesOrderDraft permission. Reuses the reassignDraftSalesOrder service.
  reassign: oc
    .input(
      z.object({
        id: UUIDSchema,
        toOrganizationId: UUIDSchema,
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(
      z.object({
        draftSalesOrderId: UUIDSchema,
        fromOrganizationId: UUIDSchema,
        toOrganizationId: UUIDSchema,
      }),
    )
    .route({
      summary: "Reassign a sales order to another organization",
      description:
        "Moves a sales order and its lines to another organization with an optional reason, logs the change, and starts that organization's order workflow.",
      method: "POST",
    }),
  // Reject a draft sales order (sets status REJECTED + logs an activity entry).
  // Gated by Approve:SalesOrderDraftV3 — rejecting is the negative of approving.
  reject: oc
    .input(
      z.object({
        id: UUIDSchema,
        reason: z.string().trim().min(1).max(500),
        /** True when rejecting because a new/revised PO is expected. */
        awaitingRevision: z.boolean().optional().default(false),
      }),
    )
    .output(z.object({ id: UUIDSchema, status: z.string() }))
    .route({
      summary: "Reject a sales order",
      description:
        "Rejects a sales order with a required reason, optionally marking it as awaiting a revised purchase order, and logs the rejection to the order's activity.",
      method: "POST",
    }),
  // Admin-only reversal of a rejection: status back to RECEIVED, rejection
  // fields cleared, rejected gate decisions dropped, and the approval chain
  // re-run. Gated by Approve:SalesOrderDraftV3 AND platform admin.
  unreject: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ id: UUIDSchema, status: z.string() }))
    .route({
      summary: "Undo a sales order rejection",
      description:
        "Admin action that returns a rejected order to received, clears the rejection and rejected approval decisions, logs it, and re-runs the approval workflow.",
      method: "POST",
    }),
  // Soft-delete a draft sales order. Gated by Delete:SalesOrderDraftV3.
  delete: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ id: UUIDSchema, deleted: z.literal(true) }))
    .route({
      summary: "Delete a sales order draft",
      description:
        "Soft-deletes a sales order draft, reactivates any earlier revision it replaced, and clears its pending approval-request notifications.",
      method: "POST",
    }),
  // Poll the ERP for the order number of a draft submitted via a REGISTERED
  // submission API (EBS assigns numbers asynchronously; the import response
  // carries none). Reads the correlation ref back out of the stored request
  // payload and updates the submission record. Gated by Read:SalesOrderDraftV3
  // — a refresh-from-ERP action any viewer of the order may take.
  pollErpOrderNumber: oc
    .input(z.object({ id: UUIDSchema }))
    .output(
      z.object({
        erpOrderNumber: z.string().nullable(),
        polled: z.boolean(),
      }),
    )
    .route({
      summary: "Fetch ERP order number for a sales order",
      description:
        "Checks the ERP for the order number assigned to a submitted sales order and saves it to the submission record when found.",
      method: "POST",
    }),
  // Build the Input Submission Map payload for a draft (explicit id, else the
  // org's most recent) WITHOUT sending, approving, or writing anything — the
  // safe way to validate a map. Admin-gated: it executes org resolver code
  // and is a config-authoring surface.
  previewErpSubmissionPayload: oc
    .input(
      z.object({
        apiDefinitionId: z.string().min(1),
        draftSalesOrderId: UUIDSchema.optional(),
      }),
    )
    .output(
      z.object({
        payload: z.record(z.string(), z.unknown()),
        docRef: z.string().nullable(),
        sampledDraft: z
          .object({
            id: UUIDSchema,
            purchaseOrderNumber: z.string().nullable(),
          })
          .nullable(),
        warnings: z.array(z.string()),
      }),
    )
    .route({
      summary: "Preview ERP submission payload for an order",
      description:
        "Builds the payload that would be sent to the ERP for a draft (or the latest one) using a submission mapping, without sending or saving anything. Admin only.",
      method: "POST",
    }),
} as const;

export type SalesOrderFieldPathInfo = z.infer<typeof FieldPathInfoSchema>;
export type SampleFieldPathsResponse = z.infer<
  typeof SampleFieldPathsResponseSchema
>;
export type SalesOrderV3ListItem = z.infer<typeof V3ListItemSchema>;
export type SalesOrderV3Detail = z.infer<typeof V3DetailSchema>;
export type SalesOrderV3SplitGroupMember = z.infer<
  typeof SplitGroupMemberSchema
>;
export type SalesOrderV3RevisionChainMember = z.infer<
  typeof RevisionChainMemberSchema
>;
export type ListSalesOrdersV3Input = z.infer<typeof ListInputSchema>;
export type ListSalesOrdersV3Response = z.infer<typeof ListResponseSchema>;
